import type { Socket } from 'socket.io-client'
import { connectSocket } from '../../lib/socket'
import { apiDelete, apiGet, apiPost } from '../../lib/apiClient'
import type { IDeploymentEngine } from './IDeploymentEngine.interface'
import type {
  ComparisonResult,
  Deployment,
  DeploymentEvent,
  DeploymentFilter,
  DeploymentInput,
  DeploymentRule,
  Environment,
  Stage,
  VerificationResult,
} from '../../types/deployment.types'

class StrideDeploymentEngine implements IDeploymentEngine {
  private readonly sockets = new Map<string, Socket>()

  // ── Environments ────────────────────────────────────────────────────────────

  listEnvironments(): Promise<Environment[]> {
    return apiGet<Environment[]>('/api/arm/environments')
  }

  getEnvironment(id: string): Promise<Environment> {
    return apiGet<Environment>(`/api/arm/environments/${id}`)
  }

  // ── Deployments ─────────────────────────────────────────────────────────────

  startDeployment(input: DeploymentInput): Promise<Deployment> {
    return apiPost<Deployment>('/api/arm/deployments', input)
  }

  getDeployment(id: string): Promise<Deployment> {
    return apiGet<Deployment>(`/api/arm/deployments/${id}`)
  }

  listDeployments(filter?: DeploymentFilter): Promise<Deployment[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const qs = filter !== undefined ? '?' + new URLSearchParams(filter as any).toString() : ''
    return apiGet<Deployment[]>(`/api/arm/deployments${qs}`)
  }

  cancelDeployment(id: string): Promise<void> {
    return apiDelete(`/api/arm/deployments/${id}`)
  }

  // ── Detail / verification ────────────────────────────────────────────────────

  getDeploymentStages(deploymentId: string): Promise<Stage[]> {
    return apiGet<Stage[]>(`/api/arm/deployments/${deploymentId}/stages`)
  }

  getVerificationResults(deploymentId: string): Promise<VerificationResult[]> {
    return apiGet<VerificationResult[]>(`/api/arm/deployments/${deploymentId}/verification`)
  }

  getDeploymentRules(): Promise<DeploymentRule[]> {
    return apiGet<DeploymentRule[]>('/api/arm/rules')
  }

  // ── Comparison ───────────────────────────────────────────────────────────────

  compareSources(
    left:  { type: 'org' | 'branch'; ref: string },
    right: { type: 'org' | 'branch'; ref: string },
  ): Promise<ComparisonResult> {
    return apiPost<ComparisonResult>('/api/compare', { left, right })
  }

  // ── Real-time subscription via Socket.IO ─────────────────────────────────────

  subscribeToDeployment(
    deploymentId: string,
    onEvent: (event: DeploymentEvent) => void,
  ): () => void {
    const socket = connectSocket({ path: '/socket.io' })

    socket.on('connect', () => {
      socket.emit('subscribe', deploymentId)
    })

    socket.on('deployment:log',      (data: DeploymentEvent) => onEvent(data))
    socket.on('deployment:stage',    (data: DeploymentEvent) => onEvent(data))
    socket.on('deployment:complete', (data: DeploymentEvent) => onEvent(data))
    socket.on('deployment:error',    (data: DeploymentEvent) => onEvent(data))

    this.sockets.set(deploymentId, socket)

    return () => {
      socket.disconnect()
      this.sockets.delete(deploymentId)
    }
  }
}

export const strideDeploymentEngine = new StrideDeploymentEngine()
